import { logError } from '@openphone/quo-observability'
import type { Disposable, IEnvironment, OperationDescriptor } from 'relay-runtime'
import { commitLocalUpdate, Store } from 'relay-runtime'

import { throttled } from '@src/lib/throttled'

interface RetainedOperation {
  operation: OperationDescriptor
  count: number
}

// Relay keeps its retained roots private, so track them through the public `retain`.
export class RetainTrackingStore extends Store {
  private readonly retainedOperations = new Map<string, RetainedOperation>()

  override retain(operation: OperationDescriptor): Disposable {
    const id = operation.request.identifier
    const entry = this.retainedOperations.get(id) ?? { operation, count: 0 }
    entry.count++
    this.retainedOperations.set(id, entry)

    const disposable = super.retain(operation)
    let disposed = false

    return {
      dispose: () => {
        if (disposed) {
          return
        }
        disposed = true
        entry.count--
        if (entry.count === 0) {
          this.retainedOperations.delete(id)
        }
        disposable.dispose()
      },
    }
  }

  getRetainedOperations(): OperationDescriptor[] {
    return Array.from(this.retainedOperations.values(), (entry) => entry.operation)
  }
}

// Invalidation alone doesn't refetch mounted queries, so refetch every retained one too.
export async function invalidateAndRefetchRetainedQueries(
  environment: IEnvironment,
): Promise<void> {
  const store = environment.getStore()
  if (!(store instanceof RetainTrackingStore)) {
    throw new Error('invalidateAndRefetchRetainedQueries requires a RetainTrackingStore')
  }

  commitLocalUpdate(environment, (recordStore) => recordStore.invalidateStore())

  // Cap concurrent requests so a screen with many queries doesn't hit the server all at once
  const refetch = throttled(
    4,
    (operation: OperationDescriptor) =>
      new Promise<void>((resolve) => {
        // Wait for complete, toPromise() would unsubscribe after the first @defer payload
        environment.execute({ operation }).subscribe({
          complete: () => resolve(),
          error: (error: Error) => {
            logError(error, { operationName: operation.request.node.params.name })
            resolve()
          },
        })
      }),
  )

  const operations = store.getRetainedOperations().filter(({ request }) => {
    const { params } = request.node
    // Client-only queries have nothing to send, matching Relay's OperationExecutor check
    return params.operationKind === 'query' && (params.id != null || params.text != null)
  })

  await Promise.all(operations.map((operation) => refetch(operation)))
}
